import React, { useEffect, useState, createElement } from 'react';
import { KeyIcon, EyeIcon, CodeIcon, DownloadIcon, PackageIcon, HeadphonesIcon, GiftIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
export function SolutionSection() {
  const [showDigitalBonusDetails, setShowDigitalBonusDetails] = useState(false);
  const [showPhysicalBonusDetails, setShowPhysicalBonusDetails] = useState(false);
  const [showBundleMeditationDetails, setShowBundleMeditationDetails] = useState(false);
  const {
    language,
    isLoading
  } = useLanguage();
  // Load Wistia player scripts
  useEffect(() => {
    // Load player.js
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    playerScript.async = true;
    document.body.appendChild(playerScript);
    // Load embed scripts for all videos
    const videoIds = ['ebfuijeuia', 'yl77d8j996', 'rm7kh6yew5', '51uvwo1jg9', 'mppna6mupr', 'w31theb23h', 'xbtr852q56', 'nym8ci19mw'];
    const embedScripts = videoIds.map(id => {
      const embedScript = document.createElement('script');
      embedScript.src = `https://fast.wistia.com/embed/${id}.js`;
      embedScript.async = true;
      embedScript.type = 'module';
      document.body.appendChild(embedScript);
      return embedScript;
    });
    return () => {
      document.body.removeChild(playerScript);
      embedScripts.forEach(script => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      });
    };
  }, []);
  // Don't render until language detection is complete
  if (isLoading) {
    return null;
  }
  const t = translations[language]?.solution;
  // Basic safety check for core properties
  if (!t || !t.features || !t.benefits) {
    return null;
  }
  // Spanish checkout link
  const digitalLink = language === 'es' ? 'https://www.voicesoffreedomus.com/cart/45280183124154:1' : 'https://www.voicesoffreedomus.com/cart/45272484643002:1';
  // Audio URL based on language
  const bonusAudioUrl = language === 'es' ? 'https://share.deal.ai/files/6903cecd11c178905f921b0c/d87748c2-120b-4958-a11e-5ae4b9dda06b' : 'https://share.deal.ai/files/6903cbb989875f72a2b472bb/0f3dae23-19d7-4aa5-a169-bcaa8c42ac16';
  return <section id="book-section" className="py-12 md:py-20 px-4 bg-gradient-to-b from-black to-teal-950" data-id="element-208">
      <div className="max-w-6xl mx-auto" data-id="element-209">
        <div className="text-center mb-8 md:mb-12" data-id="element-210">
          <p className="text-lg md:text-2xl mb-6 md:mb-8 text-teal-200 leading-relaxed" data-id="element-211">
            {t.intro} <span className="text-yellow-400" data-id="element-212">{t.imagine}</span>{' '}
            {t.imagineText}
          </p>
        </div>
        <div className="bg-gradient-to-br from-yellow-950/30 to-black border-2 border-yellow-600/50 rounded-2xl p-6 md:p-12 mb-8 md:mb-12" data-id="element-213">
          {/* Wistia Video Testimonial - Only for Spanish */}
          {language === 'es' && <div className="mb-8 md:mb-12 max-w-md mx-auto" data-id="element-214"></div>}
          <div className={`grid ${language === 'es' ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-6 mb-8 md:mb-12`} data-id="element-215">
            {/* Digital Edition - Always shown */}
            <div className={`bg-black/50 border-2 border-yellow-600/50 rounded-xl p-6 hover:border-yellow-400 transition-all ${language === 'es' ? 'max-w-2xl mx-auto w-full' : ''}`} data-id="element-216">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-217">
                <DownloadIcon className="w-6 h-6 text-yellow-400" data-id="element-218" />
                <h3 className="text-2xl font-bold text-yellow-400" data-id="element-219">
                  {t.digital}
                </h3>
              </div>
              {/* 5 Star Rating */}
              <div className="flex justify-center gap-1 mb-4" data-id="element-220">
                {[...Array(5)].map((_, i) => <svg key={i} className="w-6 h-6 text-green-400 fill-green-400" viewBox="0 0 24 24" data-id="element-221">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-222" />
                  </svg>)}
              </div>
              <div className="flex justify-center mb-6" data-id="element-223">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oyz5GB8MJJnmomK7GwM4JC/Generated_Image_October_27%2C_2025_-_5_59PM.png" alt="Digital Edition" className="w-full max-w-sm rounded-lg shadow-2xl shadow-yellow-500/30" data-id="element-224" />
              </div>
              <div className="text-center mb-6" data-id="element-225">
                <p className="text-gray-400 line-through text-xl mb-2" data-id="element-226">$47</p>
                <p className="text-5xl font-bold text-yellow-400 mb-3" data-id="element-227">
                  $24.97
                </p>
                <p className="text-teal-200 text-lg mb-4" data-id="element-228">
                  {t.instantDownload}
                </p>
                <ul className="text-left text-gray-300 space-y-2 mb-6" data-id="element-229">
                  <li className="flex items-start gap-2" data-id="element-230">
                    <span className="text-yellow-400 mt-1" data-id="element-231">✓</span>
                    <span data-id="element-232">{t.features[0]}</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-233">
                    <span className="text-yellow-400 mt-1" data-id="element-234">✓</span>
                    <span data-id="element-235">{t.features[1]}</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-236">
                    <HeadphonesIcon className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" data-id="element-237" />
                    <span data-id="element-238">{t.features[2]}</span>
                  </li>
                </ul>
                <div className="bg-gradient-to-br from-green-950/50 to-black border-2 border-green-500/50 rounded-lg p-4 mb-6" data-id="element-239">
                  <div className="flex items-center justify-center gap-2 mb-3" data-id="element-240">
                    <GiftIcon className="w-6 h-6 text-green-400" data-id="element-241" />
                    <h4 className="text-xl font-bold text-green-400" data-id="element-242">
                      {t.bonus}
                    </h4>
                  </div>
                  <div className="flex justify-center mb-3" data-id="element-243">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dGUuFum1oJN8EtXx9jvzFz/Generated_Image_October_29%2C_2025_-_4_16PM_%282%29.png" alt="DMT: The Spirit Molecule" className="w-32 h-auto rounded shadow-lg shadow-green-500/30" data-id="element-244" />
                  </div>
                  <p className="text-white font-bold mb-2" data-id="element-245">{t.bonusTitle}</p>
                  <p className="text-sm text-gray-300 mb-2" data-id="element-246">
                    {t.bonusSubtitle}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-300 mb-2" data-id="element-247">
                    <HeadphonesIcon className="w-4 h-4 text-green-400" data-id="element-248" />
                    <span data-id="element-249">{t.bonusFormat}</span>
                  </div>
                  <p className="text-green-400 font-bold text-lg mb-3" data-id="element-250">
                    {t.bonusValue}
                  </p>
                  <div className="mb-3" data-id="element-251">
                    <audio key={`digital-bonus-${language}`} controls controlsList="nodownload" crossOrigin="anonymous" className="w-full h-10 rounded" preload="auto" style={{
                    backgroundColor: '#1f2937',
                    outline: 'none'
                  }} data-id="element-252">
                      <source src={bonusAudioUrl} type="audio/mpeg" data-id="element-253" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                  <button onClick={() => setShowDigitalBonusDetails(!showDigitalBonusDetails)} className="flex items-center justify-center gap-2 mx-auto text-green-300 hover:text-green-200 transition-colors text-sm" data-id="element-254">
                    {showDigitalBonusDetails ? <>
                        <span data-id="element-255">{t.readLess}</span>
                        <ChevronUpIcon className="w-4 h-4" data-id="element-256" />
                      </> : <>
                        <span data-id="element-257">{t.readMore}</span>
                        <ChevronDownIcon className="w-4 h-4" data-id="element-258" />
                      </>}
                  </button>
                  {showDigitalBonusDetails && <div className="mt-4 pt-4 border-t border-green-500/30 text-left space-y-3" data-id="element-259">
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-260">
                        {t.bonusDescription.intro}
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-261">
                        {t.bonusDescription.paragraph1}
                      </p>
                      <p className="text-sm text-white font-semibold" data-id="element-262">
                        {t.bonusDescription.uncover}
                      </p>
                      <ul className="text-sm text-gray-300 space-y-1 pl-4" data-id="element-263">
                        {t.bonusDescription.points.map((point, index) => <li key={index} data-id="element-264">{point}</li>)}
                      </ul>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-265">
                        {t.bonusDescription.paragraph2}
                      </p>
                      <p className="text-sm text-green-300 italic" data-id="element-266">
                        {t.bonusDescription.quote}
                      </p>
                      <div className="pt-2 text-xs text-gray-400" data-id="element-267">
                        <p className="font-semibold text-white mb-1" data-id="element-268">
                          {t.bonusDescription.detailsTitle}
                        </p>
                        <p data-id="element-269">{t.bonusDescription.format}</p>
                        <p data-id="element-270">{t.bonusDescription.length}</p>
                        <p data-id="element-271">{t.bonusDescription.genre}</p>
                      </div>
                    </div>}
                </div>
              </div>
              <a href={digitalLink} target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-yellow-600 to-yellow-700 text-black px-6 py-4 rounded-full text-lg font-bold hover:from-yellow-500 hover:to-yellow-600 transform hover:scale-105 transition-all text-center" data-id="element-272">
                {t.ctaDigital}
              </a>
            </div>
            {/* Pack Despertar Bundle - Only for Spanish */}
            {language === 'es' && <div className="bg-black/50 border-2 border-purple-600/50 rounded-xl p-6 hover:border-purple-400 transition-all max-w-2xl mx-auto w-full relative" data-id="element-273">
                {/* Best Seller Badge */}
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 py-2 rounded-full font-bold text-sm shadow-lg z-10" data-id="element-274">
                  ⭐ Best Seller
                </div>
                <div className="flex items-center justify-center gap-2 mb-4" data-id="element-275">
                  <PackageIcon className="w-6 h-6 text-purple-400" data-id="element-276" />
                  <h3 className="text-2xl font-bold text-purple-400" data-id="element-277">
                    {t.bundle?.title || 'Pack Despertar'}
                  </h3>
                </div>
                {/* 5 Star Rating */}
                <div className="flex justify-center gap-1 mb-4" data-id="element-278">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-6 h-6 text-green-400 fill-green-400" viewBox="0 0 24 24" data-id="element-279">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-280" />
                    </svg>)}
                </div>
                <div className="flex justify-center mb-6" data-id="element-281">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4MVrzFAKrfHoGQy7en26dD/BUNDLE.png" alt="Pack Despertar" className="w-full max-w-sm rounded-lg shadow-2xl shadow-purple-500/30" data-id="element-282" />
                </div>
                <div className="text-center mb-6" data-id="element-283">
                  <p className="text-gray-400 line-through text-xl mb-2" data-id="element-284">
                    $96.94
                  </p>
                  <p className="text-5xl font-bold text-purple-400 mb-3" data-id="element-285">
                    $49.97
                  </p>
                  <p className="text-purple-200 text-lg mb-4 font-semibold" data-id="element-286">
                    {t.bundle?.subtitle || 'Total Incluye:'}
                  </p>
                  <ul className="text-left text-gray-300 space-y-3 mb-6" data-id="element-287">
                    <li className="flex items-start gap-2" data-id="element-288">
                      <span className="text-purple-400 mt-1" data-id="element-289">✓</span>
                      <span data-id="element-290">
                        {t.bundle?.items?.[0] || '¿Estás listo para jugar? (audio + eBook) – Valor: US$24.97'}
                      </span>
                    </li>
                    <li className="flex flex-col gap-2" data-id="element-291">
                      <div className="flex items-start gap-2" data-id="element-292">
                        <HeadphonesIcon className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" data-id="element-293" />
                        <span data-id="element-294">
                          {t.bundle?.items?.[1] || 'Meditaciones Guiadas (audio binaural) – Valor: US$47.00'}
                        </span>
                      </div>
                      <button onClick={() => setShowBundleMeditationDetails(!showBundleMeditationDetails)} className="flex items-center justify-start gap-2 ml-6 text-purple-300 hover:text-purple-200 transition-colors text-sm" data-id="element-295">
                        {showBundleMeditationDetails ? <>
                            <span data-id="element-296">{t.readLess}</span>
                            <ChevronUpIcon className="w-4 h-4" data-id="element-297" />
                          </> : <>
                            <span data-id="element-298">{t.readMore}</span>
                            <ChevronDownIcon className="w-4 h-4" data-id="element-299" />
                          </>}
                      </button>
                      {showBundleMeditationDetails && <div className="ml-6 mt-3 p-4 bg-purple-950/30 border border-purple-500/30 rounded-lg text-left space-y-3" data-id="element-300">
                          <h4 className="text-lg font-bold text-purple-300" data-id="element-301">
                            {t.bundle?.meditationDetails?.title || 'Programa de Meditación Guiada: 30 Días para Despertar tu Ser'}
                          </h4>
                          <p className="text-sm text-purple-200 font-semibold" data-id="element-302">
                            {t.bundle?.meditationDetails?.masterclass || 'Incluye acceso a la Masterclass: "El Verdadero Secreto de los Élites" (1 hora)'}
                          </p>
                          <p className="text-sm text-gray-300 leading-relaxed" data-id="element-303">
                            {t.bundle?.meditationDetails?.intro || 'Embárcate en una travesía de 30 días hacia la expansión de tu conciencia. Este programa no es solo una serie de meditaciones: es un sistema de transformación diseñado para reprogramar tu mente, equilibrar tu energía y reconectarte con tu esencia creadora.'}
                          </p>
                          <div className="space-y-3" data-id="element-304">
                            <div className="flex items-start gap-2" data-id="element-305">
                              <SparklesIcon className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" data-id="element-306" />
                              <div data-id="element-307">
                                <p className="text-sm font-semibold text-white" data-id="element-308">
                                  {t.bundle?.meditationDetails?.experienceTitle || 'Lo que vivirás durante los 30 días'}
                                </p>
                                <p className="text-sm text-gray-300 mt-1" data-id="element-309">
                                  {t.bundle?.meditationDetails?.experienceText || 'Cada semana está meticulosamente estructurada para guiarte por cuatro fases evolutivas:'}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2 pl-7" data-id="element-310">
                              <div data-id="element-311">
                                <p className="text-sm font-semibold text-purple-300" data-id="element-312">
                                  {t.bundle?.meditationDetails?.week1Title || 'Semana 1 – Despertando la Conciencia Plena'}
                                </p>
                                <p className="text-xs text-gray-300" data-id="element-313">
                                  {t.bundle?.meditationDetails?.week1Text || 'Inicia tu práctica con meditaciones matutinas, de mediodía y vespertinas que te ayudan a silenciar la mente y restaurar tu energía. Incluye el video especial "El Verdadero Secreto de los Élites" —una clase magistral de 1 hora que expone los mecanismos mentales y emocionales que las élites utilizan para controlar la atención colectiva, y cómo liberarte de esa programación.'}
                                </p>
                              </div>
                              <div data-id="element-314">
                                <p className="text-sm font-semibold text-purple-300" data-id="element-315">
                                  {t.bundle?.meditationDetails?.week2Title || 'Semana 2 – Profundizando en la Práctica'}
                                </p>
                                <p className="text-xs text-gray-300" data-id="element-316">
                                  {t.bundle?.meditationDetails?.week2Text || 'Aprende a observar tus pensamientos sin apego y a expandir tu conciencia corporal a través de técnicas de respiración y presencia consciente.'}
                                </p>
                              </div>
                              <div data-id="element-317">
                                <p className="text-sm font-semibold text-purple-300" data-id="element-318">
                                  {t.bundle?.meditationDetails?.week3Title || 'Semana 3 – Expansión de la Conciencia'}
                                </p>
                                <p className="text-xs text-gray-300" data-id="element-319">
                                  {t.bundle?.meditationDetails?.week3Text || 'Activa la compasión, la gratitud y el amor incondicional. Más del 70 % de los practicantes reportan una disminución notable del estrés y un 82 % una claridad mental ampliada en esta fase.'}
                                </p>
                              </div>
                              <div data-id="element-320">
                                <p className="text-sm font-semibold text-purple-300" data-id="element-321">
                                  {t.bundle?.meditationDetails?.week4Title || 'Semana 4 – Integración y Trascendencia'}
                                </p>
                                <p className="text-xs text-gray-300" data-id="element-322">
                                  {t.bundle?.meditationDetails?.week4Text || 'Disuelve las limitaciones mentales y habita tu estado natural de paz inquebrantable con la Meditación de Trascendencia y la práctica final de Abundancia.'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2" data-id="element-323">
                              <TrendingUpIcon className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" data-id="element-324" />
                              <div data-id="element-325">
                                <p className="text-sm font-semibold text-white" data-id="element-326">
                                  {t.bundle?.meditationDetails?.benefitsTitle || 'Beneficios comprobados'}
                                </p>
                                <ul className="text-xs text-gray-300 space-y-1 mt-1" data-id="element-327">
                                  <li data-id="element-328">
                                    {t.bundle?.meditationDetails?.benefits?.[0] || 'Reducción del estrés y la ansiedad'}
                                  </li>
                                  <li data-id="element-329">
                                    {t.bundle?.meditationDetails?.benefits?.[1] || 'Sueño profundo y restaurador'}
                                  </li>
                                  <li data-id="element-330">
                                    {t.bundle?.meditationDetails?.benefits?.[2] || 'Claridad mental y enfoque sostenido'}
                                  </li>
                                  <li data-id="element-331">
                                    {t.bundle?.meditationDetails?.benefits?.[3] || 'Elevación energética y emocional'}
                                  </li>
                                  <li data-id="element-332">
                                    {t.bundle?.meditationDetails?.benefits?.[4] || 'Despertar intuitivo y expansión interior'}
                                  </li>
                                </ul>
                              </div>
                            </div>
                            <div className="flex items-start gap-2" data-id="element-333">
                              <GiftIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" data-id="element-334" />
                              <div data-id="element-335">
                                <p className="text-sm font-semibold text-white" data-id="element-336">
                                  {t.bundle?.meditationDetails?.includesTitle || 'Incluye además:'}
                                </p>
                                <ul className="text-xs text-gray-300 space-y-1 mt-1" data-id="element-337">
                                  <li data-id="element-338">
                                    {t.bundle?.meditationDetails?.includes?.[0] || 'Acceso completo al programa de 30 días (audio + guía digital)'}
                                  </li>
                                  <li data-id="element-339">
                                    {t.bundle?.meditationDetails?.includes?.[1] || 'Masterclass de 1 hora "El Verdadero Secreto de los Élites"'}
                                  </li>
                                  <li data-id="element-340">
                                    {t.bundle?.meditationDetails?.includes?.[2] || 'Meditaciones especiales de abundancia y manifestación'}
                                  </li>
                                  <li data-id="element-341">
                                    {t.bundle?.meditationDetails?.includes?.[3] || 'Comunidad privada de apoyo'}
                                  </li>
                                </ul>
                              </div>
                            </div>
                            <div className="pt-2 border-t border-purple-500/30" data-id="element-342">
                              <p className="text-xs text-gray-400" data-id="element-343">
                                {t.bundle?.meditationDetails?.value || 'Valor total del programa: US $47.00'}
                              </p>
                              <p className="text-xs text-purple-300 font-semibold" data-id="element-344">
                                {t.bundle?.meditationDetails?.investment || 'Tu inversión hoy: US $49.97 (incluido en el Pack Despertar Total)'}
                              </p>
                            </div>
                          </div>
                        </div>}
                    </li>
                    <li className="flex items-start gap-2" data-id="element-345">
                      <GiftIcon className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" data-id="element-346" />
                      <span data-id="element-347">
                        {t.bundle?.items?.[2] || 'BONUS: DMT – La Molécula de Dios (audio + eBook) – Valor: US$24.97'}
                      </span>
                    </li>
                  </ul>
                </div>
                <a href="https://www.voicesoffreedomus.com/cart/45290003792058:1" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 rounded-full text-lg font-bold hover:from-purple-500 hover:to-purple-600 transform hover:scale-105 transition-all text-center" data-id="element-348">
                  {t.bundle?.cta || 'OBTENER PACK DESPERTAR'}
                </a>
              </div>}
            {/* Physical Book - Only show for English users */}
            {language === 'en' && t.physicalFeatures && <div className="bg-black/50 border-2 border-teal-600/50 rounded-xl p-6 hover:border-teal-400 transition-all" data-id="element-349">
                <div className="flex items-center justify-center gap-2 mb-4" data-id="element-350">
                  <PackageIcon className="w-6 h-6 text-teal-400" data-id="element-351" />
                  <h3 className="text-2xl font-bold text-teal-400" data-id="element-352">
                    {t.physical}
                  </h3>
                </div>
                {/* 5 Star Rating */}
                <div className="flex justify-center gap-1 mb-4" data-id="element-353">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-6 h-6 text-green-400 fill-green-400" viewBox="0 0 24 24" data-id="element-354">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-355" />
                    </svg>)}
                </div>
                <div className="flex justify-center mb-6" data-id="element-356">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3QXbKH8MkBDm42326D8Lnu/Generated_Image_October_27%2C_2025_-_5_58PM.png" alt="Physical Book" className="w-full max-w-sm rounded-lg shadow-2xl shadow-teal-500/30" data-id="element-357" />
                </div>
                <div className="text-center mb-6" data-id="element-358">
                  <p className="text-gray-400 line-through text-xl mb-2" data-id="element-359">$67</p>
                  <p className="text-5xl font-bold text-teal-400 mb-3" data-id="element-360">
                    $49.97
                  </p>
                  <p className="text-teal-200 text-lg mb-1" data-id="element-361">{t.shipped}</p>
                  <p className="text-sm text-gray-400 mb-4" data-id="element-362">{t.shippingText}</p>
                  <ul className="text-left text-gray-300 space-y-2 mb-6" data-id="element-363">
                    {t.physicalFeatures?.map((feature, index) => <li key={index} className="flex items-start gap-2" data-id="element-364">
                        {index === 3 ? <HeadphonesIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-365" /> : <span className="text-teal-400 mt-1" data-id="element-366">✓</span>}
                        <span data-id="element-367">{feature}</span>
                      </li>)}
                  </ul>
                  <div className="bg-gradient-to-br from-green-950/50 to-black border-2 border-green-500/50 rounded-lg p-4 mb-6" data-id="element-368">
                    <div className="flex items-center justify-center gap-2 mb-3" data-id="element-369">
                      <GiftIcon className="w-6 h-6 text-green-400" data-id="element-370" />
                      <h4 className="text-xl font-bold text-green-400" data-id="element-371">
                        {t.bonus}
                      </h4>
                    </div>
                    <div className="flex justify-center mb-3" data-id="element-372">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dGUuFum1oJN8EtXx9jvzFz/Generated_Image_October_29%2C_2025_-_4_16PM_%282%29.png" alt="DMT: The Spirit Molecule" className="w-32 h-auto rounded shadow-lg shadow-green-500/30" data-id="element-373" />
                    </div>
                    <p className="text-white font-bold mb-2" data-id="element-374">{t.bonusTitle}</p>
                    <p className="text-sm text-gray-300 mb-2" data-id="element-375">
                      {t.bonusSubtitle}
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-300 mb-2" data-id="element-376">
                      <HeadphonesIcon className="w-4 h-4 text-green-400" data-id="element-377" />
                      <span data-id="element-378">{t.bonusFormat}</span>
                    </div>
                    <p className="text-green-400 font-bold text-lg mb-3" data-id="element-379">
                      {t.bonusValue}
                    </p>
                    <div className="mb-3" data-id="element-380">
                      <audio key={`physical-bonus-${language}`} controls controlsList="nodownload" crossOrigin="anonymous" className="w-full h-10 rounded" preload="auto" style={{
                    backgroundColor: '#1f2937',
                    outline: 'none'
                  }} data-id="element-381">
                        <source src={bonusAudioUrl} type="audio/mpeg" data-id="element-382" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                    <button onClick={() => setShowPhysicalBonusDetails(!showPhysicalBonusDetails)} className="flex items-center justify-center gap-2 mx-auto text-green-300 hover:text-green-200 transition-colors text-sm" data-id="element-383">
                      {showPhysicalBonusDetails ? <>
                          <span data-id="element-384">{t.readLess}</span>
                          <ChevronUpIcon className="w-4 h-4" data-id="element-385" />
                        </> : <>
                          <span data-id="element-386">{t.readMore}</span>
                          <ChevronDownIcon className="w-4 h-4" data-id="element-387" />
                        </>}
                    </button>
                    {showPhysicalBonusDetails && <div className="mt-4 pt-4 border-t border-green-500/30 text-left space-y-3" data-id="element-388">
                        <p className="text-sm text-gray-300 leading-relaxed" data-id="element-389">
                          {t.bonusDescription.intro}
                        </p>
                        <p className="text-sm text-gray-300 leading-relaxed" data-id="element-390">
                          {t.bonusDescription.paragraph1}
                        </p>
                        <p className="text-sm text-white font-semibold" data-id="element-391">
                          {t.bonusDescription.uncover}
                        </p>
                        <ul className="text-sm text-gray-300 space-y-1 pl-4" data-id="element-392">
                          {t.bonusDescription.points.map((point, index) => <li key={index} data-id="element-393">{point}</li>)}
                        </ul>
                        <p className="text-sm text-gray-300 leading-relaxed" data-id="element-394">
                          {t.bonusDescription.paragraph2}
                        </p>
                        <p className="text-sm text-green-300 italic" data-id="element-395">
                          {t.bonusDescription.quote}
                        </p>
                        <div className="pt-2 text-xs text-gray-400" data-id="element-396">
                          <p className="font-semibold text-white mb-1" data-id="element-397">
                            {t.bonusDescription.detailsTitle}
                          </p>
                          <p data-id="element-398">{t.bonusDescription.format}</p>
                          <p data-id="element-399">{t.bonusDescription.length}</p>
                          <p data-id="element-400">{t.bonusDescription.genre}</p>
                        </div>
                      </div>}
                  </div>
                </div>
                <a href="https://www.voicesoffreedomus.com/cart/45272737677498:1" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-4 rounded-full text-lg font-bold hover:from-teal-500 hover:to-teal-600 transform hover:scale-105 transition-all text-center" data-id="element-401">
                  {t.ctaPhysical}
                </a>
              </div>}
          </div>
          {/* Video Testimonials Carousel - Only for Spanish */}
          {language === 'es' && <div className="mb-8 md:mb-12 testimonials-section" data-id="element-402">
              <h3 className="text-2xl md:text-3xl font-bold text-center mb-6 text-purple-400" data-id="element-403">
                Mira lo que dicen nuestros lectores y meditadores de nuestra
                comunidad
              </h3>
              <div className="relative" data-id="element-404">
                <div className="overflow-x-auto pb-4 scrollbar-hide" data-id="element-405">
                  <div className="flex gap-4 min-w-max px-4" data-id="element-406">
                    {[{
                  id: 'ebfuijeuia',
                  aspect: '0.5504587155963303'
                }, {
                  id: 'yl77d8j996',
                  aspect: '0.5625'
                }, {
                  id: 'rm7kh6yew5',
                  aspect: '0.547945205479452'
                }, {
                  id: '51uvwo1jg9',
                  aspect: '0.5625'
                }, {
                  id: 'mppna6mupr',
                  aspect: '0.5625'
                }, {
                  id: 'w31theb23h',
                  aspect: '0.5625'
                }, {
                  id: 'xbtr852q56',
                  aspect: '0.547945205479452'
                }].map((video, index) => <div key={index} className="w-64 h-[450px] flex-shrink-0 rounded-lg overflow-hidden shadow-2xl shadow-purple-500/20 border-2 border-purple-500/30 bg-black" data-id="element-407">
                        <style dangerouslySetInnerHTML={{
                    __html: `
                              wistia-player[media-id='${video.id}']:not(:defined) {
                                background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${video.id}/swatch');
                                display: block;
                                filter: blur(5px);
                                padding-top: ${1 / parseFloat(video.aspect) * 100}%;
                              }
                              wistia-player[media-id='${video.id}'] {
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                              }
                            `
                  }} data-id="element-408" />
                        <wistia-player media-id={video.id} aspect={video.aspect} data-id="element-409" />
                      </div>)}
                  </div>
                </div>
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-black to-transparent pointer-events-none" data-id="element-410" />
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-black to-transparent pointer-events-none" data-id="element-411" />
              </div>
            </div>}
          <p className="text-lg md:text-xl text-center text-teal-200 mb-6 md:mb-8" data-id="element-412">
            {t.helpText}
          </p>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-413">
            <div className="bg-black/50 border border-yellow-600/30 rounded-xl p-4 md:p-6 text-center" data-id="element-414">
              <EyeIcon className="w-10 h-10 md:w-12 md:h-12 text-yellow-400 mx-auto mb-3 md:mb-4" data-id="element-415" />
              <p className="text-base md:text-lg text-gray-200" data-id="element-416">
                {t.benefits[0]}
              </p>
            </div>
            <div className="bg-black/50 border border-yellow-600/30 rounded-xl p-4 md:p-6 text-center" data-id="element-417">
              <KeyIcon className="w-10 h-10 md:w-12 md:h-12 text-yellow-400 mx-auto mb-3 md:mb-4" data-id="element-418" />
              <p className="text-base md:text-lg text-gray-200" data-id="element-419">
                {t.benefits[1]}
              </p>
            </div>
            <div className="bg-black/50 border border-yellow-600/30 rounded-xl p-4 md:p-6 text-center" data-id="element-420">
              <CodeIcon className="w-10 h-10 md:w-12 md:h-12 text-yellow-400 mx-auto mb-3 md:mb-4" data-id="element-421" />
              <p className="text-base md:text-lg text-gray-200" data-id="element-422">
                {t.benefits[2]}
              </p>
            </div>
          </div>
          {/* Testimonials Section */}
          <div className="mt-12 md:mt-16" data-id="element-423">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-yellow-400" data-id="element-424">
              {language === 'es' ? 'Lo que dicen nuestros lectores' : 'What Our Readers Say'}
            </h3>
            <div className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-425">
              {translations[language].proof.testimonials.map((testimonial, index) => <div key={index} className="bg-gradient-to-br from-teal-950/50 to-black border border-teal-700/30 rounded-xl p-4 md:p-6" data-id="element-426">
                    <div className="flex gap-1 mb-3 md:mb-4" data-id="element-427">
                      {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-lg" data-id="element-428">
                          ★
                        </span>)}
                    </div>
                    <p className="text-base md:text-lg italic text-gray-200 mb-3 md:mb-4" data-id="element-429">
                      "{testimonial.quote}"
                    </p>
                    <p className="text-sm md:text-base text-teal-300 font-semibold" data-id="element-430">
                      — {testimonial.author}
                    </p>
                  </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}